import React, { useState, useEffect, useRef } from 'react';

// ==========================================
// 1. LOGIN SCREEN COMPONENT
// ==========================================
const LoginScreen = ({ onLoginSuccess }) => {
    const [workspace, setWorkspace] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');

        let siteUrl = workspace.trim().toLowerCase();
        if (!siteUrl.startsWith('http')) siteUrl = 'https://' + siteUrl;
        siteUrl = siteUrl.replace(/\/$/, ""); 

        try {
            const response = await fetch(`${siteUrl}/wp-json/fcomify/v1/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                localStorage.setItem('fcomify_chat_token', data.token);
                localStorage.setItem('fcomify_workspace_url', siteUrl);
                localStorage.setItem('fcomify_teammate_name', data.username); 
                onLoginSuccess();
            } else {
                setError(data.message || 'Login failed. Please check your credentials.');
            }
        } catch (err) {
            setError("Could not connect to this Workspace. Please ensure the FCOMIFY plugin is installed and activated.");
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 font-sans">
            <div className="bg-white max-w-md w-full rounded-2xl shadow-xl p-8">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center text-white text-3xl mx-auto mb-4">💬</div>
                    <h2 className="text-2xl font-black text-slate-900">Sign in to your Workspace</h2>
                </div>
                {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm font-bold">{error}</div>}
                <form onSubmit={handleLogin} className="space-y-5">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Workspace URL</label>
                        <input type="text" placeholder="e.g. mystore.com" value={workspace} onChange={(e) => setWorkspace(e.target.value)} required className="w-full px-4 py-3 rounded-lg border border-slate-300 outline-none focus:border-blue-500 bg-slate-50" />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Teammate Username</label>
                        <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} required className="w-full px-4 py-3 rounded-lg border border-slate-300 outline-none focus:border-blue-500 bg-slate-50" />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Password</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-3 rounded-lg border border-slate-300 outline-none focus:border-blue-500 bg-slate-50" />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-slate-900 text-white font-bold py-3 rounded-lg hover:bg-blue-600 transition shadow-md disabled:opacity-50 mt-2">
                        {loading ? 'Connecting...' : 'Login'}
                    </button>
                </form>
            </div>
        </div>
    );
};

// ==========================================
// 2. LIVE CHAT DASHBOARD COMPONENT (WhatsApp Clone)
// ==========================================
const ChatDashboard = ({ onLogout }) => {
    const teammateName = localStorage.getItem('fcomify_teammate_name');
    const token = localStorage.getItem('fcomify_chat_token');
    const siteUrl = localStorage.getItem('fcomify_workspace_url');

    const [activeChat, setActiveChat] = useState(null);
    const [messageText, setMessageText] = useState('');
    const [contacts, setContacts] = useState([]);
    const [messages, setMessages] = useState([]);
    const [templates, setTemplates] = useState([]); 
    const [searchQuery, setSearchQuery] = useState('');
    
    // Emojis, Uploads, and Modals
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showNewChatModal, setShowNewChatModal] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false); // NEW: Dropdown Menu State
    const [chatMediaUrl, setChatMediaUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    
    const [newChatPhone, setNewChatPhone] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');

    const chatEndRef = useRef(null);
    const fileInputRef = useRef(null);

    const commonEmojis = ['😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇','🙂','🙃','😉','😌','😍','🥰','😘','😋','😛','😜','🤪','🤨','🧐','🤓','😎','🤩','🥳','😏','😒','😞','😔','😟','😕','🙁','☹️','😣','😖','😫','😩','🥺','😢','😭','😤','😠','😡','🤯','😳','🥵','🥶','😱','😨','😰','😥','😓','🤗','🤔','🤭','🤫','🤥','😶','😐','😑','😬','🙄','😯','😦','😧','😮','😲','🥱','😴','🤤','😪','😵','🤐','🥴','🤢','🤮','🤧','😷','🤒','🤕','🤑','🤠','👍','👎','👌','✌️','🤞','🫰','🤟','🤘','🤙','👈','👉','👆','👇','☝️','✋','🤚','🖐','🖖','👋','💪','🖕','✍️','🙏','❤️','🔥','✨','💯','✅','❌'];

    const fetchConversations = async () => {
        try {
            const res = await fetch(`${siteUrl}/wp-json/fcomify/v1/chat/conversations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) setContacts(data.data);
        } catch (err) { console.error("Error fetching conversations", err); }
    };

    const fetchMessages = async (phone) => {
        try {
            const res = await fetch(`${siteUrl}/wp-json/fcomify/v1/chat/messages?phone=${phone}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) setMessages(data.data);
        } catch (err) { console.error("Error fetching messages", err); }
    };

    // Fetch approved synced templates
    const fetchTemplates = async () => {
        try {
            const res = await fetch(`${siteUrl}/wp-json/fcomify/v1/chat/templates`, {
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            const data = await res.json();
            if (data.success) setTemplates(data.data);
        } catch (err) { console.error("Error fetching templates", err); }
    };

    useEffect(() => {
        fetchConversations();
        fetchTemplates();
        const interval = setInterval(() => {
            fetchConversations();
            if (activeChat) fetchMessages(activeChat.phone);
        }, 3000);
        return () => clearInterval(interval);
    }, [activeChat]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // --- NEW: PWA HOME SCREEN BADGE NOTIFICATIONS ---
    useEffect(() => {
        if ('setAppBadge' in navigator) {
            // Calculate total unread messages across all contacts
            const totalUnread = contacts.reduce((sum, contact) => sum + (Number(contact.unread) || 0), 0);
            
            if (totalUnread > 0) {
                navigator.setAppBadge(totalUnread).catch(error => console.error("App Badge Error:", error));
            } else {
                navigator.clearAppBadge().catch(error => console.error("Clear Badge Error:", error));
            }
        }
    }, [contacts]);

    const handleSelectChat = (contact) => {
        setActiveChat(contact);
        setMessages([]); 
        fetchMessages(contact.phone);
        setShowEmojiPicker(false);
        setChatMediaUrl('');
    };

    const handleBackToList = () => {
        setActiveChat(null);
        setMessages([]);
        setShowEmojiPicker(false);
        setChatMediaUrl('');
    };

    const handleAttachmentClick = () => {
        fileInputRef.current.click(); 
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = null; 
        if (file.size > 16 * 1024 * 1024) {
            alert("File is too large. Maximum size is 16MB.");
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await fetch(`${siteUrl}/wp-json/fcomify/v1/chat/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData 
            });
            const data = await res.json();
            if (data.success) setChatMediaUrl(data.url);
            else alert("Upload failed: " + data.message);
        } catch (err) { alert("Network error during upload."); }
        setIsUploading(false);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if ((!messageText.trim() && !chatMediaUrl) || !activeChat) return;
        
        const textToSend = messageText;
        const mediaToSend = chatMediaUrl;
        
        setMessageText(''); 
        setChatMediaUrl('');
        setShowEmojiPicker(false);
        
        const displayMessage = mediaToSend ? `[Attachment: ${mediaToSend}]\n${textToSend}` : textToSend;
        setMessages(prev => [...prev, { id: Date.now(), text: displayMessage, sender: 'agent', time: '...' }]);

        try {
            const res = await fetch(`${siteUrl}/wp-json/fcomify/v1/chat/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ phone: activeChat.phone, message: displayMessage }) 
            });
            const data = await res.json();
            if (data.success) { fetchMessages(activeChat.phone); } 
            else { alert("Failed to send: " + data.message); }
        } catch (err) { alert("Network error trying to send message."); }
    };

    const handleStartNewChat = async (e) => {
        e.preventDefault();
        if (!newChatPhone.trim() || !selectedTemplate) return;

        const cleanedPhone = newChatPhone.replace(/\D/g, ''); 
        const targetTemplate = templates.find(t => t.template_name === selectedTemplate);
        const templateBody = targetTemplate ? targetTemplate.template_content : `[Template: ${selectedTemplate}]`;

        setShowNewChatModal(false);

        const temporaryContact = { phone: cleanedPhone, name: 'New Lead', lastMsg: templateBody, time: 'Now', unread: 0 };
        setActiveChat(temporaryContact);
        setMessages([{ id: Date.now(), text: templateBody, sender: 'agent', time: 'Sending...' }]);

        try {
            const res = await fetch(`${siteUrl}/wp-json/fcomify/v1/chat/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ phone: cleanedPhone, message: templateBody, template_name: selectedTemplate }) 
            });
            const data = await res.json();
            
            if (data.success) {
                setNewChatPhone('');
                setSelectedTemplate('');
                fetchConversations();
                fetchMessages(cleanedPhone);
            } else {
                alert("Template Dispatch Failed: " + data.message);
                setActiveChat(null);
            }
        } catch (err) {
            alert("Network connection error.");
            setActiveChat(null);
        }
    };

    const filteredContacts = contacts.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        c.phone.includes(searchQuery)
    );

    return (
        <div className="flex h-screen bg-white font-sans overflow-hidden select-none text-slate-800 relative">
            
            {/* SIDEBAR */}
            <div className={`${activeChat ? 'hidden md:flex' : 'flex'} w-full md:w-1/3 max-w-md bg-white border-r border-slate-200 flex-col`}>
                
                <div className="px-4 py-3 flex justify-between items-center bg-white">
                    <h1 className="text-2xl font-bold text-[#25D366] tracking-tight">Fcomify</h1>
                    <div className="flex gap-4 text-slate-500 text-xl items-center relative">
                        <button onClick={() => setShowNewChatModal(true)} title="New Chat with Template" className="text-xl hover:bg-slate-100 p-1.5 rounded-full transition">➕</button>
                        
                        {/* --- NEW: DROPDOWN MENU --- */}
                        <div className="relative">
                            <button 
                                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                                title="Menu" 
                                className="text-2xl leading-none -mt-2 p-1 focus:outline-none hover:bg-slate-100 rounded-full transition"
                            >
                                ⋮
                            </button>
                            {isMenuOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)}></div>
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden animate-fade-in">
                                        <div className="px-4 py-3 border-b border-slate-100 text-xs text-slate-500">
                                            Logged in as <strong className="block text-slate-800 text-sm mt-0.5">{teammateName || 'Agent'}</strong>
                                        </div>
                                        <button 
                                            onClick={() => { setIsMenuOpen(false); onLogout(); }} 
                                            className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 font-bold text-sm transition"
                                        >
                                            Sign Out
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                        {/* -------------------------- */}
                    </div>
                </div>

                <div className="px-4 pb-3">
                    <div className="bg-[#f0f2f5] rounded-full flex items-center px-4 py-2">
                        <span className="text-slate-500 text-sm">🔍</span>
                        <input 
                            type="text" 
                            placeholder="Ask Meta AI or Search" 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-transparent border-none outline-none ml-3 w-full text-[15px]" 
                        />
                    </div>
                </div>

                <div className="px-4 pb-2 flex gap-2 overflow-x-auto text-sm border-b border-slate-100 no-scrollbar">
                    <button className="bg-[#e8fbf0] text-[#1da851] px-4 py-1.5 rounded-full font-medium whitespace-nowrap">All</button>
                    <button className="bg-[#f0f2f5] text-slate-600 px-4 py-1.5 rounded-full font-medium whitespace-nowrap">Unread</button>
                    <button className="bg-[#f0f2f5] text-slate-600 px-4 py-1.5 rounded-full font-medium whitespace-nowrap">Groups</button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {filteredContacts.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">No conversations found.</div>}
                    {filteredContacts.map((contact, idx) => (
                        <div 
                            key={idx} 
                            onClick={() => handleSelectChat(contact)} 
                            className={`flex items-center px-4 py-3 cursor-pointer hover:bg-[#f5f6f6] transition ${activeChat?.phone === contact.phone ? 'bg-[#f0f2f5]' : ''}`}
                        >
                            <div className="w-12 h-12 bg-slate-300 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-slate-600 text-lg mr-3">
                                {contact.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0 border-b border-slate-100 pb-3">
                                <div className="flex justify-between items-center mb-0.5">
                                    <h3 className="font-semibold text-[16px] truncate pr-2">{contact.name}</h3>
                                    <span className={`text-xs whitespace-nowrap ${contact.unread > 0 ? 'text-[#25D366] font-medium' : 'text-slate-500'}`}>
                                        {contact.time}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <p className="text-[14px] text-slate-500 truncate pr-2">{contact.lastMsg || 'No messages yet'}</p>
                                    {contact.unread > 0 && (
                                        <span className="bg-[#25D366] text-white text-[11px] font-bold px-1.5 min-w-[20px] h-[20px] flex items-center justify-center rounded-full flex-shrink-0">
                                            {contact.unread}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex justify-around items-center border-t border-slate-200 py-2 bg-white pb-safe">
                    <div className="flex flex-col items-center text-slate-800">
                        <span className="text-xl bg-[#dcf8c6] px-6 py-1 rounded-full mb-1">💬</span>
                        <span className="text-[11px] font-bold">Chats</span>
                    </div>
                </div>
            </div>

            {/* MAIN CHAT AREA */}
            <div className={`${activeChat ? 'flex' : 'hidden md:flex'} w-full md:flex-1 flex-col bg-[#efeae2] relative`}>
                {activeChat ? (
                    <>
                        <div className="bg-white px-2 py-2 border-b border-slate-200 flex items-center shadow-sm z-10 cursor-pointer">
                            <button onClick={handleBackToList} className="md:hidden p-2 text-slate-600 font-bold text-xl mr-1 hover:bg-slate-100 rounded-full">
                                &larr;
                            </button>
                            <div className="w-10 h-10 bg-slate-300 rounded-full flex items-center justify-center mr-3 text-slate-600 font-bold flex-shrink-0">
                                {activeChat.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="font-semibold text-[16px] text-slate-800 truncate">{activeChat.name}</h2>
                                <p className="text-[12px] text-slate-500 truncate">+{activeChat.phone}</p>
                            </div>
                            <div className="flex gap-4 text-slate-500 px-3 text-xl">
                                <button>⋮</button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat bg-opacity-50 relative">
                            {messages.map((msg) => (
                                <div key={msg.id} className={`flex ${msg.sender === 'agent' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[85%] md:max-w-[65%] rounded-lg px-3 py-1.5 shadow-sm relative ${msg.sender === 'agent' ? 'bg-[#dcf8c6] rounded-tr-none' : 'bg-white rounded-tl-none'}`}>
                                        
                                        {msg.text.includes('[Attachment:') && (
                                            <div className="mb-2 p-2 bg-black/5 rounded text-xs text-blue-600 break-all">
                                                <a href={msg.text.match(/\[Attachment: (.*?)\]/)?.[1]} target="_blank" rel="noreferrer" className="hover:underline">
                                                    📎 View Attached File
                                                </a>
                                            </div>
                                        )}
                                        
                                        <p className="text-[15px] leading-snug text-slate-800 whitespace-pre-wrap" style={{wordBreak: 'break-word'}}>
                                            {msg.text.replace(/\[Attachment: .*?\]\n/, '')}
                                        </p>
                                        <div className="text-[10px] text-slate-500 text-right mt-0.5 flex justify-end items-center gap-1">
                                            {msg.time}
                                            {msg.sender === 'agent' && <span className="text-blue-500 text-xs">✓✓</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div ref={chatEndRef} />
                        </div>

                        <div className="bg-[#f0f2f5] p-2.5 relative">
                            
                            {showEmojiPicker && (
                                <div className="absolute bottom-[60px] left-2 bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-72 h-64 overflow-y-auto flex flex-wrap gap-2 z-50">
                                    {commonEmojis.map(emoji => (
                                        <button key={emoji} onClick={() => { setMessageText(prev => prev + emoji); setShowEmojiPicker(false); }} className="text-2xl hover:bg-slate-100 p-1 rounded transition">
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {chatMediaUrl && (
                                <div className="absolute bottom-[60px] left-14 bg-white border border-slate-200 rounded-lg shadow-xl p-2 text-sm flex items-center gap-2 z-50 max-w-xs">
                                    <span className="truncate text-blue-600">📎 Attached File Ready</span>
                                    <button onClick={() => setChatMediaUrl('')} className="text-red-500 font-bold ml-2">✕</button>
                                </div>
                            )}

                            <div className="flex items-end gap-2">
                                <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} accept="image/*,video/mp4,application/pdf,.doc,.docx,audio/*" />
                                <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className={`p-2 text-2xl transition ${showEmojiPicker ? 'text-[#00a884]' : 'text-slate-500'}`}>😊</button>
                                <button type="button" onClick={handleAttachmentClick} disabled={isUploading} className={`p-2 text-xl -ml-2 transition ${chatMediaUrl ? 'text-[#00a884]' : 'text-slate-500'} ${isUploading ? 'opacity-50' : ''}`}>
                                    {isUploading ? '⏳' : '📎'}
                                </button>
                                <div className="flex-1 bg-white rounded-2xl flex items-center shadow-sm">
                                    <textarea value={messageText} onChange={(e) => setMessageText(e.target.value)} onClick={() => setShowEmojiPicker(false)} placeholder="Message" className="w-full bg-transparent border-none px-4 py-3 outline-none resize-none max-h-32 text-[15px]" rows="1" onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e); } }} />
                                </div>
                                {(messageText.trim() || chatMediaUrl) ? (
                                    <button onClick={handleSendMessage} className="bg-[#00a884] text-white rounded-full w-11 h-11 flex items-center justify-center flex-shrink-0 shadow-sm transition transform hover:scale-105">
                                        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M1.101 21.757 23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path></svg>
                                    </button>
                                ) : (
                                    <button className="bg-[#00a884] text-white rounded-full w-11 h-11 flex items-center justify-center flex-shrink-0 shadow-sm opacity-50 cursor-not-allowed">🎤</button>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 hidden md:flex flex-col items-center justify-center text-center px-4 border-b-[6px] border-b-[#25D366]">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/1200px-WhatsApp.svg.png" alt="WhatsApp" className="w-24 h-24 mb-8 opacity-20 grayscale" />
                        <h2 className="text-3xl font-light text-slate-700 mb-4">Fcomify Web</h2>
                        <p className="text-slate-500 max-w-md text-sm">Send and receive messages without keeping your phone online.<br/>Use Fcomify on up to 4 linked devices and 1 phone at the same time.</p>
                    </div>
                )}
            </div>

            {/* NEW CHAT MODAL OVERLAY */}
            {showNewChatModal && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-[999] animate-fade-in">
                    <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden animate-slide-up">
                        <div className="bg-[#00a884] text-white px-6 py-4 flex justify-between items-center">
                            <h3 className="font-bold text-lg">Send Template Message</h3>
                            <button onClick={() => { setShowNewChatModal(false); setNewChatPhone(''); setSelectedTemplate(''); }} className="text-2xl font-light hover:opacity-70">&times;</button>
                        </div>
                        <form onSubmit={handleStartNewChat} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Customer Phone Number</label>
                                <input 
                                    type="text" 
                                    placeholder="e.g. 88017XXXXXXXX" 
                                    value={newChatPhone}
                                    onChange={(e) => setNewChatPhone(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-[#00a884] font-medium text-[15px]"
                                    required 
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Select Synced Template</label>
                                <select 
                                    value={selectedTemplate}
                                    onChange={(e) => setSelectedTemplate(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-[#00a884] font-medium text-[15px]"
                                    required
                                >
                                    <option value="" disabled>-- Choose Approved Template --</option>
                                    {templates.map(t => (
                                        <option key={t.id} value={t.template_name}>{t.template_name}</option>
                                    ))}
                                </select>
                            </div>
                            
                            {selectedTemplate && (
                                <div className="p-3 bg-[#e1f5fe] border border-blue-200 rounded-lg text-slate-700 text-sm max-h-32 overflow-y-auto whitespace-pre-wrap">
                                    <strong className="text-xs text-blue-800 block mb-1">Template Content Preview:</strong>
                                    {templates.find(t => t.template_name === selectedTemplate)?.template_content}
                                </div>
                            )}

                            <div className="flex gap-3 pt-2 justify-end">
                                <button type="button" onClick={() => { setShowNewChatModal(false); setNewChatPhone(''); setSelectedTemplate(''); }} className="px-4 py-2 text-slate-500 hover:bg-slate-100 font-semibold rounded-lg text-sm transition">Cancel</button>
                                <button type="submit" disabled={!newChatPhone || !selectedTemplate} className="px-5 py-2 bg-[#00a884] text-white font-semibold rounded-lg text-sm transition hover:bg-[#009675] shadow-sm disabled:opacity-40">Send Template</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==========================================
// 3. MAIN APP ROUTER
// ==========================================
function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('fcomify_chat_token'));

    const handleLoginSuccess = () => setIsLoggedIn(true);

    const handleLogout = () => {
        localStorage.removeItem('fcomify_chat_token');
        localStorage.removeItem('fcomify_workspace_url');
        localStorage.removeItem('fcomify_teammate_name');
        setIsLoggedIn(false);
    };

    return isLoggedIn ? <ChatDashboard onLogout={handleLogout} /> : <LoginScreen onLoginSuccess={handleLoginSuccess} />;
}

export default App;